import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PermissionGate from '../../../components/common/PermissionGate';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import CustomAlert from '../../../components/common/CustomAlert';
import hrService from '../../../api/services/hrService';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  Download,
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Eye,
  RefreshCw,
  FileSpreadsheet,
  AlertTriangle,
  Info,
  X
} from 'lucide-react';

const BulkUpload = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { alertState, showSuccess, showError, hideAlert } = useCustomAlert();

  // State management
  const [uploadState, setUploadState] = useState('idle'); // idle, uploading, validation, success, partial, error
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // NOTE: Validation and import are fully handled via backend APIs.

  const handleDownloadTemplate = async () => {
    try {

      // Call the actual API
      const result = await hrService.downloadBulkTemplate();

      if (result.success) {
        toast.success('Excel template downloaded successfully!');
      } else {
        showError(`Failed to download template: ${result.error}`);
      }
    } catch (error) {
      showError('Failed to download template. Please try again.');
    }
  };

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);

  const processFile = (file) => {
    if (file) {
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

      // Check file extension as fallback for some systems where mime type might be missing or different
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const allowedExtensions = ['xls', 'xlsx'];

      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        showError('Please select a valid Excel file (.xlsx or .xls)');
        return;
      }

      if (file.size > 25 * 1024 * 1024) { // 25MB limit
        showError('File size must be less than 25MB');
        return;
      }

      setSelectedFile(file);
      setUploadState('idle');
      setValidationResults(null);
      setImportResults(null);
    }
  };

  const handleFileSelect = (event) => {
    processFile(event.target.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent flickering: only disable drag state if leaving the area completely
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setUploadState('uploading');

    try {

      // Call the actual API
      const result = await hrService.bulkUploadEmployees(selectedFile);

      if (result.success) {
        setValidationResults(result.data);
        setUploadState('validation');
        setShowValidationModal(true);
      } else {
        setUploadState('error');
        showError(`Upload failed: ${result.error}`);
      }
    } catch (error) {
      setUploadState('error');
      showError('Failed to upload file. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!validationResults) return;

    setIsProcessing(true);
    setUploadState('uploading');

    try {

      // Pass the validData from validation results
      const validRows = validationResults.validData || [];

      // Call the actual API
      const result = await hrService.bulkImportEmployees(validRows);

      if (result.success) {
        setImportResults(result.data);

        if (result.data.failed === 0) {
          setUploadState('success');
          showSuccess(`All ${result.data.successful} employees imported successfully!`);
        } else if (result.data.successful > 0) {
          setUploadState('partial');
          showSuccess(`${result.data.successful} employees imported successfully. ${result.data.failed} failed.`);
        } else {
          setUploadState('error');
          showError('No employees were imported successfully.');
        }
      } else {
        setUploadState('error');
        showError(`Import failed: ${result.error}`);
      }
    } catch (error) {
      setUploadState('error');
      showError('Failed to import employees. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setValidationResults(null);
    setImportResults(null);
    setUploadState('idle');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStateIcon = () => {
    switch (uploadState) {
      case 'uploading':
        return <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />;
      case 'validation':
        return <FileText className="h-5 w-5 text-yellow-600" />;
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'partial':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Upload className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStateMessage = () => {
    switch (uploadState) {
      case 'uploading':
        return 'Processing file...';
      case 'validation':
        return 'Validation complete. Review errors before importing.';
      case 'success':
        return 'All records imported successfully!';
      case 'partial':
        return 'Some records imported successfully. Check errors below.';
      case 'error':
        return 'Import failed. Please check errors and try again.';
      default:
        return 'Drag and drop an Excel file here, or click to select';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex">
        <button
          onClick={() => navigate('/employee_list')}
          className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bulk Upload</h1>
          <p className="text-gray-600 mt-1">Import multiple employee records via Excel</p>
        </div>
        <div className="w-40"></div>
      </div>

      {/* Template Download Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <FileSpreadsheet className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-lg font-semibold text-gray-900">Download Template</h2>
          </div>
          <PermissionGate permission="employee_management" action="read">
            <button
              onClick={handleDownloadTemplate}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Excel Template
            </button>
          </PermissionGate>
        </div>
        <p className="text-gray-600 text-sm">
          Download the template file to ensure proper formatting. Fill in employee data and upload the completed file.
        </p>
      </div>

      {/* File Upload Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload File</h2>

        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
            }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className="hidden"
          />

          <div className="flex flex-col items-center">
            {getStateIcon()}
            <p className="mt-2 text-gray-600">{getStateMessage()}</p>
            <p className="mt-1 text-xs text-gray-500">
              Supported formats: XLS, XLSX • Maximum file size: 25 MB
            </p>

            {selectedFile && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-gray-600 mr-2" />
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-medium text-gray-700">{selectedFile.name}</span>
                    <span className="text-xs text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReset();
                  }}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors text-gray-500 hover:text-red-500"
                  title="Remove file"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {!selectedFile && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="mt-4 px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Select File
              </button>
            )}
          </div>
        </div>

        {selectedFile && (
          <div className="mt-4 flex justify-center">
            <PermissionGate permission="employee_management" action="create">
              <button
                onClick={handleUpload}
                disabled={isProcessing}
                className="flex items-center px-6 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors disabled:opacity-50"
              >
                {isProcessing ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Upload & Validate
              </button>
            </PermissionGate>
          </div>
        )}
      </div>


      {/* Import Results */}
      {importResults && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Import Results</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{importResults.totalProcessed}</div>
              <div className="text-sm text-blue-800">Total Processed</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{importResults.successful}</div>
              <div className="text-sm text-green-800">Successfully Imported</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{importResults.failed}</div>
              <div className="text-sm text-red-800">Failed</div>
            </div>
          </div>

          {importResults.errors.length > 0 && (
            <div className="mt-4">
              <h3 className="text-md font-semibold text-gray-900 mb-2">Import Errors</h3>
              <div className="space-y-2">
                {importResults.errors.map((error, index) => (
                  <div key={index} className="flex items-center p-3 bg-red-50 rounded-lg">
                    <AlertCircle className="h-4 w-4 text-red-600 mr-3 flex-shrink-0" />
                    <div className="text-sm text-red-800">
                      Row {error.row}: {error.error}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-center space-x-4">
            <button
              onClick={() => navigate('/employee_list')}
              className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Eye className="h-4 w-4 mr-2" />
              View Employee List
            </button>
            <button
              onClick={handleReset}
              className="flex items-center px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Upload Another File
            </button>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 rounded-lg p-6">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-md font-semibold text-blue-900 mb-3">Important Notes</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
              <ul className="space-y-1 list-disc list-inside">
                <li><span className="font-semibold">Use the template:</span> Always download and use the latest Excel template so column headers match exactly.</li>
                <li><span className="font-semibold">Required per row:</span> Employee ID, First Name, Email, Phone, Date of Birth, Gender, PAN Number, Aadhaar Number.</li>
                <li><span className="font-semibold">Contact details:</span> Email must be valid (e.g. name@example.com). Phone must be exactly 10 digits (numbers only).</li>
              </ul>
              <ul className="space-y-1 list-disc list-inside">
                <li><span className="font-semibold">Enums:</span> Gender = male / female / other.</li>
                <li><span className="font-semibold">IDs & numbers:</span> PAN = ABCDE1234F, Aadhaar = 12 digits. Employee ID must be unique and up to 20 characters.</li>
                <li><span className="font-semibold">Import behaviour:</span> Any row with validation errors is skipped; only fully valid rows are imported. Support for 1000+ records.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Validation Summary Modal */}
      {showValidationModal && validationResults && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setShowValidationModal(false)}
          />

          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-agrosaf-green/10 rounded-lg">
                  <FileSpreadsheet className="h-5 w-5 text-agrosaf-green" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Validation Summary</h2>
                  <p className="text-sm text-gray-500">
                    Review successful and failed rows before importing employees.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowValidationModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700">
                    {validationResults.totalRows}
                  </div>
                  <div className="text-sm text-blue-700 font-medium">Total Rows</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-700">
                    {validationResults.validRows}
                  </div>
                  <div className="text-sm text-green-700 font-medium">Successful Rows</div>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-700">
                    {validationResults.invalidRows}
                  </div>
                  <div className="text-sm text-red-700 font-medium">Failed Rows</div>
                </div>
              </div>

              {/* Failed rows table */}
              {validationResults.errors && validationResults.errors.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Failed Rows & Reasons
                  </h3>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b">
                      <p className="text-xs font-medium text-gray-700">
                        Showing {validationResults.errors.length} validation errors
                      </p>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Row
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Field
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Value
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                              Reason
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {validationResults.errors.map((error, index) => (
                            <tr key={index} className="align-top">
                              <td className="px-3 py-2 text-xs text-gray-800 font-mono">
                                {error.row}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-800">
                                {error.field}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-600 break-all">
                                {error.value ?? '-'}
                              </td>
                              <td className="px-3 py-2 text-xs text-red-700">
                                {error.error}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-gray-50 space-x-3">
              <button
                onClick={() => setShowValidationModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              {validationResults.validRows > 0 && (
                <button
                  onClick={() => {
                    setShowValidationModal(false);
                    handleImport();
                  }}
                  disabled={isProcessing}
                  className="flex items-center px-5 py-2 text-sm font-medium text-white bg-agrosaf-green rounded-lg hover:bg-agrosaf-darkGreen transition-colors disabled:opacity-50"
                >
                  {isProcessing ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Import {validationResults.validRows} Valid Rows
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Alert */}
      <CustomAlert
        isOpen={alertState.show}
        onClose={hideAlert}
        title={alertState.title}
        message={alertState.message}
        type={alertState.type}
        showCancel={alertState.showCancel}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
      />
    </div>
  );
};


export default BulkUpload;
